<template>
  <a-layout
    style="min-width: 190px"
    width="100%"
  >
    <a-auto-complete
      :value="value"
      :loading="loading"
      @update:value="handleChange"
      style="width: 100%"
      :options="filteredData"
      @search="handleSearch"
    >
      <template #option="{ value: value, label: label }">
        <div
          style="
            font-size: 14px;
            color: #2f2f2f;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          "
        >
          {{ label }}
        </div>
        <div
          style="
            font-size: 12px;
            color: #999;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          "
        >
          {{ value }}
        </div>
      </template>
      <template #default>
        <a-input
          :placeholder="placeholder"
          :value="value"
          :allow-clear="field.allowClear == undefined ? true : field.allowClear"
          size="small"
          :maxlength="maxlength"
        >
          <template
            v-if="field?.prefix || field?.prefixIcon"
            #prefix
          >
            <span v-if="field?.prefix">{{ field.prefix }}</span>
            <component
              v-if="field?.prefixIcon"
              :is="field?.prefixIcon"
            />
          </template>
          <template
            v-if="field?.addonBefore || field?.addonBeforeIcon"
            #addonBefore
          >
            <span v-if="field?.addonBefore">{{ field.addonBefore }}</span>
            <component
              v-if="field?.addonBeforeIcon"
              :is="field?.addonBeforeIcon"
            />
          </template>
          <template
            v-if="field?.addonAfter || field?.addonAfterIcon"
            #addonAfter
          >
            <span v-if="field?.addonAfter">{{ field.addonAfter }}</span>
            <component
              v-if="field?.addonAfterIcon"
              :is="field?.addonAfterIcon"
            />
          </template>
          <template #suffix>
            <LoadingOutlined v-if="loading" />
            <span
              v-else
              :title="exactMatch(value)?.label"
              class="column-name-field-suffix"
            >
              {{ exactMatch(value)?.label }}
            </span>
            <span v-if="field?.suffix">{{ field.suffix }}</span>
            <component
              v-if="field?.suffixIcon"
              :is="field?.suffixIcon"
            />
          </template>
        </a-input>
      </template>
    </a-auto-complete>
  </a-layout>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { debounce } from 'lodash-es';
import { convertValueByClassName, isNotEmpty } from 'liyu-pc-base/utils/function';
import BoldMatcher from 'liyu-pc-base/components/FormRender/RemoteSelect/boldenText.vue';
import { getApiData } from 'liyu-pc-base/api/api-model';
import { useRequiredConditionStore, required_condition_event_obj } from 'liyu-pc-base/init-plugin';

type Field = {
  mode: 'multiple' | 'tags' | 'combobox' | 'default';
  cb?: any;
  select_style: any;
  default: string;
  name: string;
  postUrl: string;
  searchConditions: any;
  queryType: string;
  simpleSearchConditions: any;
  maxTagCount: number;
  allowClear: boolean;
  defaultValue: any;
  disabled: boolean;
  show_search: boolean;
  searchColor: string;
  required_condition: any;
  defaultValueType: string;
  globalDefaultKey: string;
  globalDefaultField: string;
};
export default defineComponent({
  name: 'RemoteSelectPro',
  props: {
    field: {
      type: Object as PropType<Field>,
      required: true,
    },
    valueKey: {
      type: String,
    },
    labelKeyType: {
      type: Number,
      default: () => 0,
    },
    labelKey: {
      type: String,
    },
    labelSpace: {
      type: String,
      default: '-',
    },
    labelKeyList: {
      type: Array,
      default: () => [],
    },
    value: {
      type: [Array, Number, String],
      required: false,
      default: () => null,
    },
    label: {
      type: [Array, String],
      required: false,
      default: () => [],
    },
    formModel: {
      type: Object,
      required: true,
    },
    outModel: {
      type: Object,
      default: () => {},
    },
    placeholder: {
      type: String,
    },
    init_model: {
      type: Object,
      default: () => {},
    },
    is_input: {
      type: Boolean,
      default: () => false,
    },
    init_watch: {
      // 普通查询监听字段 {监听key: 传参key}
      type: Object,
      default: () => {},
    },
    is_form_design: {
      type: Boolean,
      default: () => false,
    },
    maxlength: {
      type: Number,
      default: () => 100,
    },
  },
  emits: ['update:value'],
  setup(props, { emit }) {
    const loading = ref(false);
    const modalRef = reactive({
      selectedRows: new Array<number | any>(),
      datasource: new Array<any>(),
    });
    const filteredData = ref([]);
    const requiredConditionStore = useRequiredConditionStore();
    const handleSearch = (str: any) => {
      filteredData.value = datasource.value
        .filter((item: any) => item.value?.includes(str) || item.label?.includes(str))
        .map((item: any) => ({
          value: item.value,
          label: item.label,
        }));
    };
    const exactMatch = (value: any) => {
      const column = datasource.value.find((item: any) => item.value === value);
      return column as any;
    };
    const getConditionsItem = (value: any, list: any, item: any) => {
      if (item.operator === 'not_blank_not_null') {
        list.push({
          field_name: item.field_name,
          operator: item.operator,
          value: [],
          format: item.format,
          class_name: item.class_name,
        });
      } else if (value instanceof Array) {
        const [first] = value;
        if (isNotEmpty(first)) {
          list.push({
            field_name: item.field_name,
            operator: item.operator,
            value: convertValueByClassName(value, item.class_name),
            format: item.format,
            class_name: item.class_name,
          });
        }
      } else if (isNotEmpty(value)) {
        let val = value;
        if (item.operator === 'in' || item.operator === 'notin') {
          if (value.includes('[')) {
            val = JSON.parse(value);
          } else if (value.includes(',')) {
            val = value.split(',');
          }
          if (item.class_name === 'int') {
            val = val.map(Number);
          }
        }
        list.push({
          field_name: item.field_name,
          operator: item.operator,
          value: convertValueByClassName(val instanceof Array ? val : [val], item.class_name),
          format: item.format,
          class_name: item.class_name,
        });
      }
    };
    /**
     * searchType === 1 时按 key 同时处理 outModel 与 formModel：
     * outModel 中该字段有值（非 undefined / null）时优先，否则回退 formModel 的同名字段。
     * 不能用 `outModel ? outModel : formModel` 做对象级二选一（outModel 默认为 {} 恒为真值）。
     */
    const resolveModelValue = (selectName: any): { found: boolean; value: any } => {
      if (
        props.outModel &&
        Object.prototype.hasOwnProperty.call(props.outModel, selectName) &&
        props.outModel[selectName] !== undefined &&
        props.outModel[selectName] !== null
      ) {
        return { found: true, value: props.outModel[selectName] };
      }
      if (props.formModel && Object.prototype.hasOwnProperty.call(props.formModel, selectName)) {
        return { found: true, value: props.formModel[selectName] };
      }
      return { found: false, value: undefined };
    };
    const getConditions = () => {
      const conditions = [];
      let search_conditions = [
        [
          {
            searchType: 3,
            field_name: 'is_active',
            operator: 'eq',
            value: [true],
            format: 'choices',
            class_name: 'bool',
          },
        ],
      ];
      if (props.field.searchConditions) {
        search_conditions = props.field.searchConditions;
      }
      search_conditions.forEach((item: any) => {
        const list = [];
        item.forEach((item1: any) => {
          if (item1.searchType === 1) {
            // outModel 优先、formModel 兜底，两者同时参与查找
            const resolved = resolveModelValue(item1.select_name);
            if (resolved.found) {
              getConditionsItem(resolved.value, list, item1);
            }
          } else if (item1.searchType === 2) {
            Object.keys(props.formModel).forEach((key: any) => {
              if (item1.select_name == key) {
                getConditionsItem(props.formModel[key], list, item1);
              }
            });
          } else {
            getConditionsItem(item1.value, list, item1);
          }
        });
        conditions.push(list);
      });
      return conditions;
    };
    const getSimpleSearchParams = () => {
      const params = {};
      if (props.field.simpleSearchConditions) {
        props.field.simpleSearchConditions.forEach((item: any) => {
          if (item.searchType === 1) {
            // outModel 优先、formModel 兜底，两者同时参与查找
            const resolved = resolveModelValue(item.select_name);
            if (resolved.found) {
              params[item.field_name] = resolved.value;
            }
          } else if (item.searchType === 3) {
            params[item.field_name] = item.value;
          }
        });
        return params;
      }
    };

    let init_datasource = []; // 初始化datasource
    let is_refresh = false; // 是否触发刷新
    const searchConfig: any = {
      current: 1,
      pageSize: 200,
      is_pagination: true,
      is_active: true,
      ...props.init_model, // 普通查询条件
      column_fields: [],
      conditions: [],
      simpleSearchParams: {},
    };
    if (props.field.queryType === 'simple') {
      searchConfig.simpleSearchParams = getSimpleSearchParams();
    } else {
      searchConfig.conditions = getConditions();
    }
    const get_value_key = () => {
      return props.valueKey ? (props.valueKey as string) : 'id';
    };
    const get_label_key = () => {
      return props.labelKey ? (props.labelKey as string) : 'name';
    };
    const getColumnFields = () => {
      const list = [];
      if (props?.labelKeyList && props?.labelKeyList?.length > 0 && props.labelKeyType !== 1) {
        list.push(get_value_key(), ...props.labelKeyList);
      } else {
        list.push(get_value_key(), get_label_key());
      }
      searchConfig.column_fields = [].concat(list);
    };
    getColumnFields();
    let is_filter = false; // 是否调用接口过滤
    let selectData = []; // 已选择数据列表

    const datasource = computed(() => {
      return modalRef.datasource
        .map(item => {
          // 已经选择的数据不处理labelKeyList
          if (
            props.labelKeyList &&
            props.labelKeyList.length > 0 &&
            props?.labelKeyType !== 1 &&
            !selectData.some(item1 => item.id === item1.id)
          ) {
            const label = [];
            for (const key of props.labelKeyList) {
              label.push(item[key as string]);
            }
            return {
              value: item[get_value_key()],
              label: label.join(props.labelSpace),
            };
          } else {
            return {
              value: item[get_value_key()],
              label: item[get_label_key()],
            };
          }
        })
        .filter(item => item.value);
    });

    watch(
      // outModel 参与 searchType===1 的取值（见 resolveModelValue），必须一并监听：
      // 否则外部模型变化时查询条件不会重建。下方 JSON.stringify(searchConfig) 比对会挡住
      // 参数未变化时的重复刷新，不会多打请求。
      () => [props.formModel, props.outModel],
      () => {
        const oldSearch = JSON.stringify(searchConfig);
        if (props.field.queryType === 'simple') {
          searchConfig.simpleSearchParams = getSimpleSearchParams();
        } else {
          searchConfig.conditions = getConditions();
        }
        if (props.init_watch) {
          Object.keys(props.init_watch).forEach((key: any) => {
            searchConfig[props.init_watch[key]] = props.formModel[key];
          });
        }
        if (oldSearch == JSON.stringify(searchConfig)) {
          return;
        } else {
          debounce1();
        }
      },
      { deep: true },
    );

    const handleRequiredConditionChange = (key: string) => {
      if (props.field.required_condition) {
        const item = props.field.required_condition.find((item: any) => item.key === key);
        if (item) {
          debounce1();
        }
      }
    };
    onMounted(() => {
      required_condition_event_obj().on('required_condition_change', handleRequiredConditionChange);
      refresh();
    });
    onUnmounted(() => {
      required_condition_event_obj().off(
        'required_condition_change',
        handleRequiredConditionChange,
      );
    });

    if (props.field.mode === 'default') {
      if (props.value || props.value === 0) {
        if (isNaN(Number(props.value))) {
          modalRef.selectedRows.push(props.value);
        } else {
          modalRef.selectedRows.push(Number(props.value));
        }
      }
    } else {
      if (typeof props.value == 'string') {
        (props.value as string).split(',').map((item: any) => {
          modalRef.selectedRows.push(Number(item));
        });
      } else if (props.value instanceof Array) {
        props.value.map((item: any) => {
          if (isNaN(Number(item))) {
            modalRef.selectedRows.push(item);
          } else {
            modalRef.selectedRows.push(Number(item));
          }
        });
      } else if (props.value != null) {
        modalRef.selectedRows.push(props.value);
      }
    }
    const handleChange = (value: any) => {
      emit('update:value', value);
    };
    const getRequiredConditionObj = () => {
      const required_condition = props.field.required_condition;
      const required_condition_obj = {};
      if (required_condition) {
        required_condition.forEach((item: any) => {
          const value = requiredConditionStore.value_map[item.key];
          if (value !== undefined && value !== null) {
            required_condition_obj[item.field_name] = value;
          }
        });
      }
      return required_condition_obj;
    };
    const refresh = async () => {
      modalRef.datasource.length = 0;
      if (props.field.postUrl) {
        let params: any = {};
        if (props.field.queryType === 'simple') {
          params = {
            ...searchConfig.simpleSearchParams,
            required_condition: getRequiredConditionObj(),
          };
        } else {
          params = { ...searchConfig, required_condition: getRequiredConditionObj() };
          delete params.simpleSearchParams;
        }
        loading.value = true;
        try {
          const res = await getApiData(params, props.field.postUrl, false, { skip_error: true });
          is_filter = res && res.total > searchConfig.pageSize;
          init_datasource = res?.data || [];
          const filteredArray = init_datasource.filter(
            item1 => !selectData.some(item2 => item1.id === item2.id),
          );
          modalRef.datasource = selectData.concat(filteredArray);
          if (props.field.defaultValueType === 'first' && !props.is_form_design) {
            if (!props.formModel[props.field.name]) {
              if (props.field.mode === 'default' || !props.field.mode) {
                emit('update:value', modalRef.datasource[0][get_value_key()]);
              } else {
                emit('update:value', [modalRef.datasource[0][get_value_key()]]);
              }
            }
          } else if (props.field.defaultValueType === 'global' && !props.is_form_design) {
            if (!props.formModel[props.field.name]) {
              const globalDefaultKey = props.field.globalDefaultKey;
              const globalDefaultField = props.field.globalDefaultField;
              const globalDefaultValue = requiredConditionStore.value_map[globalDefaultKey];
              if (globalDefaultValue) {
                const globalDefaultItem = modalRef.datasource.find(
                  item1 => item1[globalDefaultField] === globalDefaultValue,
                );
                if (globalDefaultItem) {
                  if (props.field.mode === 'default' || !props.field.mode) {
                    emit('update:value', globalDefaultItem[get_value_key()]);
                  } else {
                    emit('update:value', [globalDefaultItem[get_value_key()]]);
                  }
                }
              }
            }
          }
          handleSearch(props.value || '');
        } finally {
          loading.value = false;
        }
      }
    };
    const debounce1: any = debounce(() => {
      refresh();
    }, 1000);
    watch(
      () => [
        props.labelKeyList,
        props.labelKey,
        props.labelKeyType,
        props.valueKey,
        props.labelSpace,
        props.field.postUrl,
      ],
      () => {
        getColumnFields();
        is_refresh = true;
      },
      { deep: true },
    );
    watch(
      () => [
        props.field.searchConditions,
        props.field.queryType,
        props.field.simpleSearchConditions,
      ],
      () => {
        if (props.field.queryType === 'simple') {
          searchConfig.simpleSearchParams = getSimpleSearchParams();
        } else {
          searchConfig.conditions = getConditions();
        }
        is_refresh = true;
      },
      { deep: true },
    );

    return {
      handleSearch,
      exactMatch,
      filteredData,
      modalRef,
      datasource,
      handleChange,
      loading,
    };
  },

  data() {
    return {
      model: this.value,
    };
  },
  components: { BoldMatcher },
});
</script>
